import clsx from 'clsx';
import { useId, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Spinner } from '@/components/ui/Spinner';

const variants = {
  primary:
    'rounded-xl bg-brand-700 text-white hover:bg-brand-800 shadow-sm active:scale-[0.98] dark:bg-brand-600 dark:hover:bg-brand-700',
  secondary:
    'rounded-xl bg-surface-raised text-default border border-default hover:bg-surface-sunken shadow-sm active:scale-[0.98]',
  outline:
    'rounded-xl bg-transparent text-brand-700 border-[1.5px] border-brand-600 hover:bg-brand-50 dark:text-brand-400 dark:border-brand-500 dark:hover:bg-brand-950/40',
  accent:
    'rounded-xl bg-accent-500 text-default hover:bg-accent-600 shadow-sm active:scale-[0.98]',
  ghost: 'rounded-xl text-default hover:bg-surface-sunken active:scale-[0.98]',
  danger: 'rounded-xl bg-grenada-red text-white hover:brightness-90 shadow-sm active:scale-[0.98]',
};

const sizes = {
  sm: 'min-h-11 px-3 py-1.5 text-xs gap-1.5',
  md: 'min-h-11 px-4 py-2 text-sm gap-2',
  lg: 'min-h-11 px-5 py-2.5 text-sm gap-2',
};

export function Button({
  className,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabledReason,
  disabled,
  children,
  title,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  loading?: boolean;
  /** Shown via title tooltip + aria-describedby when the control is disabled. */
  disabledReason?: string;
}) {
  const hintId = useId();
  const isDisabled = Boolean(disabled || loading);
  const reason = isDisabled ? disabledReason : undefined;

  return (
    <span className={clsx('inline-flex', className)}>
      <button
        {...props}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        aria-describedby={reason ? hintId : undefined}
        title={reason ?? title}
        className={clsx(
          'inline-flex items-center justify-center font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-50',
          variants[variant],
          sizes[size],
        )}
      >
        {loading ? <Spinner size={size === 'sm' ? 'sm' : 'md'} /> : null}
        {children as ReactNode}
      </button>
      {reason ? (
        <span id={hintId} className="sr-only">
          {reason}
        </span>
      ) : null}
    </span>
  );
}
